import { Head, useForm } from "@inertiajs/react"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Data Entry",
    href: "/stocks/new",
  },
]

export default function NewStock() {
  const { data, setData, post, errors, processing } = useForm({
    client_id: "",
    contract_id: "",
    symbol: "",
    operation_date: "",
    quantity: "",
    purchase_price: "",
    currency: "USD",
    commission: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/stocks", {
      onSuccess: () => {
        // Form will reset on success
      },
      onError: () => {
        // Errors will be in the errors object
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Data Entry" />

      <div className="flex flex-col gap-5 p-4">
        <h1 className="text-3xl font-bold tracking-tight">Stock Data Entry</h1>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>New Stock Entry</CardTitle>
            <CardDescription>
              Enter stock transaction details manually
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID</Label>
                  <Input
                    id="client_id"
                    value={data.client_id}
                    onChange={(e) => setData("client_id", e.target.value)}
                    placeholder="CLI001"
                  />
                  {errors.client_id && (
                    <p className="text-sm text-red-500">{errors.client_id}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract_id">Contract ID</Label>
                  <Input
                    id="contract_id"
                    value={data.contract_id}
                    onChange={(e) => setData("contract_id", e.target.value)}
                    placeholder="CTR001"
                  />
                  {errors.contract_id && (
                    <p className="text-sm text-red-500">{errors.contract_id}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="symbol">Stock Symbol</Label>
                  <Input
                    id="symbol"
                    value={data.symbol}
                    onChange={(e) => setData("symbol", e.target.value)}
                    placeholder="AAPL"
                  />
                  {errors.symbol && (
                    <p className="text-sm text-red-500">{errors.symbol}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="operation_date">Operation Date</Label>
                  <Input
                    id="operation_date"
                    type="date"
                    value={data.operation_date}
                    onChange={(e) => setData("operation_date", e.target.value)}
                  />
                  {errors.operation_date && (
                    <p className="text-sm text-red-500">{errors.operation_date}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity</Label>
                  <Input
                    id="quantity"
                    type="number"
                    step="0.0001"
                    value={data.quantity}
                    onChange={(e) => setData("quantity", e.target.value)}
                    placeholder="100"
                  />
                  {errors.quantity && (
                    <p className="text-sm text-red-500">{errors.quantity}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase Price</Label>
                  <Input
                    id="purchase_price"
                    type="number"
                    step="0.0001"
                    value={data.purchase_price}
                    onChange={(e) => setData("purchase_price", e.target.value)}
                    placeholder="150.25"
                  />
                  {errors.purchase_price && (
                    <p className="text-sm text-red-500">{errors.purchase_price}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={data.currency}
                    onValueChange={(value) => setData("currency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="MXN">MXN</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.currency && (
                    <p className="text-sm text-red-500">{errors.currency}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commission">Commission</Label>
                  <Input
                    id="commission"
                    type="number"
                    step="0.01"
                    value={data.commission}
                    onChange={(e) => setData("commission", e.target.value)}
                    placeholder="0.00"
                  />
                  {errors.commission && (
                    <p className="text-sm text-red-500">{errors.commission}</p>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={processing} className="w-full">
                {processing ? "Submitting..." : "Submit Stock Entry"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
